/**
 * EditProviderInstanceDialog - Modal para editar/liberar instância de provider
 * 
 * Permite ao admin:
 * - Preencher credenciais dinâmicas baseadas no schema do provider
 * - Liberar instância para o tenant
 * - Atualizar notas e status de pagamento
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Unlock, Save, Building2, CreditCard, Key, AlertTriangle, Server, AlertCircle, Info } from "lucide-react";
import { ProviderInstance, useProviderInstancesAdmin } from "@/hooks/useProviderInstances";
import { useProvidersList } from "@/hooks/useAddonManagement";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { showToast } from "@/lib/toast";

// Interface para informações de instância duplicada
interface DuplicateInstanceInfo {
  id: string;
  tenantName: string;
  tenantSlug: string;
  instanceName: string | null;
  status: string;
}

// Interface para campo do schema de credenciais
// Suporta campos de texto, password e select com opções dinâmicas
// Campos readonly são preenchidos automaticamente via OAuth (ex: meta-whatsapp)
interface CredentialSchemaField {
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
  options?: Array<{ label: string; value: string }>; // Opções para campos do tipo 'select'
  default?: string; // Valor padrão do campo
  readonly?: boolean; // Campos readonly são preenchidos via OAuth, não pelo admin
  hidden?: boolean; // Campos hidden não são exibidos no form
}

interface EditProviderInstanceDialogProps {
  instance: ProviderInstance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditProviderInstanceDialog({
  instance,
  open,
  onOpenChange,
}: EditProviderInstanceDialogProps) {
  // Estados do form
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [credentialsSchema, setCredentialsSchema] = useState<CredentialSchemaField[]>([]);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [instanceName, setInstanceName] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<string>("pending");
  const [duplicateError, setDuplicateError] = useState<DuplicateInstanceInfo | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string>(""); // Estado para o provider selecionado

  // Hook de mutações
  const { updateInstance, releaseInstance } = useProviderInstancesAdmin();
  
  // Lista de providers disponíveis
  const { data: providers, isLoading: isLoadingProviders } = useProvidersList();
  
  // Verifica se o provider foi alterado (para exibir alerta)
  const providerChanged = instance && selectedProviderId && selectedProviderId !== instance.provider_id;
  
  // Bloqueia alteração de provider quando instância está em uso
  const isProviderChangeBlocked = instance?.status === 'in_use';

  /**
   * Busca informações da instância que já está usando o instance_id
   * para exibir mensagem de erro detalhada ao admin
   */
  const fetchDuplicateInstanceInfo = async (instanceId: string): Promise<DuplicateInstanceInfo | null> => {
    try {
      const { data, error } = await supabase
        .from('provider_instances')
        .select(`
          id,
          instance_name,
          status,
          tenant:tenants(name, slug)
        `)
        .eq('instance_id', instanceId)
        .neq('id', instance?.id || '') // Exclui a instância atual
        .maybeSingle();

      if (error || !data) return null;

      const tenantData = data.tenant as { name: string; slug: string } | null;
      return {
        id: data.id,
        tenantName: tenantData?.name || 'Desconhecido',
        tenantSlug: tenantData?.slug || 'N/A',
        instanceName: data.instance_name,
        status: data.status,
      };
    } catch (err) {
      logger.error('EditProviderInstanceDialog', 'Erro ao buscar instância duplicada', err);
      return null;
    }
  };

  // Helper para processar formato array { fields: [...] } do config_schema
  const processArraySchema = (fields: Array<{
    name: string;
    label?: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
    description?: string;
  }>): CredentialSchemaField[] => {
    return fields.map((field) => ({
      key: field.name,
      label: field.label || field.name,
      type: field.type || 'text',
      required: field.required !== false,
      placeholder: field.placeholder || field.label || field.name,
      description: field.description,
    })) as unknown as CredentialSchemaField[];
  };

  // Helper para processar formato objeto { field: {...config} }
  // Preserva options, default, readonly e hidden para campos dinâmicos
  const processObjectSchema = (schema: Record<string, CredentialSchemaField>): CredentialSchemaField[] => {
    return Object.entries(schema)
      .filter(([key]) => key !== 'client_token') // Oculta client_token (preenchido via env var)
      .map(([key, config]) => ({
        key,
        label: config.label || key,
        type: config.type || 'text',
        required: config.required !== false,
        placeholder: config.placeholder || config.label || key,
        description: config.description,
        options: config.options, // Preserva opções para campos select
        default: config.default, // Preserva valor default
        readonly: config.readonly || false, // Preserva readonly (OAuth fields)
        hidden: config.hidden || false, // Preserva hidden
      })) as unknown as CredentialSchemaField[];
  };

  // Buscar schema de credenciais do provider
  const fetchCredentialsSchema = async (providerId: string) => {
    setLoadingSchema(true);
    try {
      // Primeiro tenta buscar versão default do provider
      const { data: versionData, error: versionError } = await supabase
        .from('provider_versions')
        .select('config')
        .eq('provider_id', providerId)
        .eq('is_default', true)
        .maybeSingle();

      if (!versionError && versionData?.config) {
        const config = versionData.config as { credentials_schema?: Record<string, CredentialSchemaField> };
        if (config.credentials_schema) {
          setCredentialsSchema(processObjectSchema(config.credentials_schema));
          return;
        }
      }

      // Fallback: buscar de api_providers.config_schema
      const { data: providerData, error: providerError } = await supabase
        .from('api_providers')
        .select('config_schema')
        .eq('id', providerId)
        .single();

      if (!providerError && providerData?.config_schema) {
        const configSchema = providerData.config_schema as { fields?: Array<{ name: string; label?: string; type?: string; required?: boolean; placeholder?: string; description?: string }> } | Record<string, CredentialSchemaField>;
        
        // Verifica se é formato array { fields: [...] }
        if ('fields' in configSchema && Array.isArray(configSchema.fields)) {
          setCredentialsSchema(processArraySchema(configSchema.fields));
          return;
        }
        
        // Formato objeto padrão
        if (typeof configSchema === 'object') {
          setCredentialsSchema(processObjectSchema(configSchema as Record<string, CredentialSchemaField>));
          return;
        }
      }

      // Fallback final: campos padrão
      logger.warn('EditProviderInstanceDialog', 'Usando schema padrão (fallback)', { providerId });
      setCredentialsSchema([
        { key: 'token', label: 'Token', type: 'password', required: true, placeholder: 'Token de acesso' } as unknown as CredentialSchemaField,
        { key: 'instance_id', label: 'Instance ID', type: 'text', required: true, placeholder: 'ID da instância' } as unknown as CredentialSchemaField,
      ]);
    } catch (error) {
      logger.error('EditProviderInstanceDialog', 'Erro ao buscar schema', error);
      // Fallback em caso de erro
      setCredentialsSchema([
        { key: 'token', label: 'Token', type: 'password', required: true } as unknown as CredentialSchemaField,
        { key: 'instance_id', label: 'Instance ID', type: 'text', required: true } as unknown as CredentialSchemaField,
      ]);
    } finally {
      setLoadingSchema(false);
    }
  };

  // Preencher form quando instância mudar
  useEffect(() => {
    if (instance) {
      // Inicializa o provider selecionado
      setSelectedProviderId(instance.provider_id);
      
      // Buscar schema do provider
      fetchCredentialsSchema(instance.provider_id);
      
      // Preencher dados existentes
      const existingCredentials = (instance as unknown as { credentials?: Record<string, string> }).credentials || {};
      // Se credentials JSONB está vazio, usa token e instance_id legados
      if (Object.keys(existingCredentials).length === 0) {
        setCredentials({
          token: instance.token || "",
          instance_id: instance.instance_id || "",
        });
      } else {
        setCredentials(existingCredentials);
      }
      
      setInstanceName(instance.instance_name || "");
      setNotes(instance.notes || "");
      setPaymentStatus(instance.payment_status);
    }
  }, [instance]);

  // Reset quando dialog fecha
  useEffect(() => {
    if (!open) {
      setCredentials({});
      setCredentialsSchema([]);
      setInstanceName("");
      setNotes("");
      setPaymentStatus("pending");
      setDuplicateError(null); // Limpa erro de duplicidade ao fechar
      setSelectedProviderId(""); // Limpa provider selecionado ao fechar
    }
  }, [open]);

  // Verifica se todos os campos obrigatórios estão preenchidos
  // IMPORTANTE: Campos readonly e hidden são ignorados na validação pois são preenchidos via OAuth ou têm valor default
  const allRequiredFilled = credentialsSchema.every((field) => {
    const fieldWithKey = field as unknown as { 
      key: string; 
      required?: boolean;
      readonly?: boolean;
      hidden?: boolean;
      type?: string;
    };
    // Campos readonly são preenchidos automaticamente via OAuth (ex: meta-whatsapp)
    if (fieldWithKey.readonly) return true;
    // Campos hidden não são editáveis pelo admin (ex: connection_type)
    if (fieldWithKey.hidden || fieldWithKey.type === 'hidden') return true;
    // Campos não obrigatórios são ignorados
    if (!fieldWithKey.required) return true;
    // Valida se campo obrigatório está preenchido
    return credentials[fieldWithKey.key]?.trim();
  });
  
  // Verifica se o provider atual é OAuth-based (ex: meta-whatsapp)
  // Providers OAuth têm campos readonly que serão preenchidos pelo fluxo de conexão
  const isOAuthProvider = credentialsSchema.some((field) => {
    const f = field as unknown as { readonly?: boolean };
    return f.readonly === true;
  });
  
  // Obtém o slug do provider selecionado
  const selectedProviderSlug = providers?.find(p => p.id === selectedProviderId)?.slug || instance?.provider?.slug;

  // Handler de liberação
  const handleRelease = async () => {
    if (!instance || !allRequiredFilled) return;
    
    // Limpa erro anterior
    setDuplicateError(null);
    
    try {
      await releaseInstance.mutateAsync({
        id: instance.id,
        credentials,
        instanceName: instanceName || undefined,
      });
      
      onOpenChange(false);
    } catch (error: unknown) {
      // Verifica se é erro de duplicidade de instance_id
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('idx_provider_instances_unique_instance') || 
          errorMessage.includes('duplicate key')) {
        const instanceIdValue = credentials.instance_id;
        if (instanceIdValue) {
          const duplicateInfo = await fetchDuplicateInstanceInfo(instanceIdValue);
          if (duplicateInfo) {
            setDuplicateError(duplicateInfo);
            showToast.error('Instance ID já em uso', 'Este Instance ID já está vinculado a outra instância.');
            return;
          }
        }
      }
      // Erro genérico já tratado pelo hook
      logger.error('EditProviderInstanceDialog', 'Erro ao liberar instância', error);
    }
  };

  // Handler de salvamento simples
  const handleSave = async () => {
    if (!instance) return;
    
    // Limpa erro anterior
    setDuplicateError(null);
    
    try {
      await updateInstance.mutateAsync({
        id: instance.id,
        data: {
          // Inclui provider_id somente se foi alterado e não está bloqueado
          ...(providerChanged && !isProviderChangeBlocked ? { provider_id: selectedProviderId } : {}),
          credentials,
          token: credentials.token || credentials.api_token || undefined,
          instance_id: credentials.instance_id || undefined,
          instance_name: instanceName || undefined,
          notes: notes || undefined,
          payment_status: paymentStatus as 'pending' | 'paid' | 'failed' | 'refunded' | 'free_trial' | 'courtesy',
        },
      });
      
      onOpenChange(false);
    } catch (error: unknown) {
      // Verifica se é erro de duplicidade de instance_id
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('idx_provider_instances_unique_instance') || 
          errorMessage.includes('duplicate key')) {
        const instanceIdValue = credentials.instance_id;
        if (instanceIdValue) {
          const duplicateInfo = await fetchDuplicateInstanceInfo(instanceIdValue);
          if (duplicateInfo) {
            setDuplicateError(duplicateInfo);
            showToast.error('Instance ID já em uso', 'Este Instance ID já está vinculado a outra instância.');
            return;
          }
        }
      }
      // Erro genérico já tratado pelo hook
      logger.error('EditProviderInstanceDialog', 'Erro ao salvar instância', error);
    }
  };

  const isLoading = updateInstance.isPending || releaseInstance.isPending;
  const canRelease = instance?.status === 'pending' && allRequiredFilled;

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Editar Instância</DialogTitle>
          <DialogDescription>
            Configure as credenciais da instância para liberar ao tenant
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 h-0 min-h-0 pr-4">
          <div className="space-y-6 py-4">
            {/* Info do Tenant */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="font-medium">{instance.tenant?.name}</p>
                <p className="text-sm text-muted-foreground">{instance.tenant?.slug}</p>
              </div>
              <Badge variant="outline">
                {instance.provider?.display_name || instance.provider?.name}
              </Badge>
            </div>

            <Separator />

            {/* Seleção de Provider */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Server className="h-4 w-4 text-muted-foreground" />
                Provider da Instância
              </Label>
              
              {/* Alerta de bloqueio para instâncias em uso */}
              {isProviderChangeBlocked && (
                <Alert className="mb-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Alteração Bloqueada</AlertTitle>
                  <AlertDescription>
                    Não é possível alterar o provider de uma instância que está em uso.
                    Primeiro desvincule a instância da inbox.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Alerta de mudança de provider */}
              {providerChanged && !isProviderChangeBlocked && (
                <Alert variant="destructive" className="mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Atenção: Mudança de Provider</AlertTitle>
                  <AlertDescription>
                    Alterar o provider irá limpar as credenciais existentes, 
                    pois cada provider possui campos diferentes.
                  </AlertDescription>
                </Alert>
              )}
              
              <Select 
                value={selectedProviderId} 
                onValueChange={(value) => {
                  setSelectedProviderId(value);
                  // Limpar credenciais ao mudar provider
                  if (value !== instance.provider_id) {
                    setCredentials({});
                    fetchCredentialsSchema(value);
                  }
                }}
                disabled={isProviderChangeBlocked || isLoadingProviders}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers?.map(provider => (
                    <SelectItem key={provider.id} value={provider.id}>
                      <div className="flex items-center gap-2">
                        <span>{provider.name}</span>
                        <Badge variant="outline" className="text-xs">{provider.slug}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Campos de Credenciais Dinâmicos */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium">Credenciais do Provider</h4>
                <Badge variant="secondary" className="text-xs">
                  {providers?.find(p => p.id === selectedProviderId)?.slug || instance.provider?.slug}
                </Badge>
              </div>

              {loadingSchema ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando campos...</span>
                </div>
              ) : (
                <>
                  {/* Alerta informativo para providers OAuth (ex: meta-whatsapp) */}
                  {isOAuthProvider && (
                    <Alert className="mb-4 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <AlertTitle className="text-blue-800 dark:text-blue-200">Provider OAuth</AlertTitle>
                      <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
                        As credenciais (WABA ID, Access Token, Phone Number ID, etc.) serão preenchidas 
                        automaticamente quando o tenant conectar via Facebook Business. 
                        Apenas libere a instância para o tenant.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Renderiza apenas campos não-readonly e não-hidden */}
                  {credentialsSchema
                    .filter((field) => {
                      const f = field as unknown as { type?: string; readonly?: boolean; hidden?: boolean };
                      // Não exibe campos hidden
                      if (f.type === 'hidden' || f.hidden) return false;
                      // Não exibe campos readonly (serão preenchidos via OAuth)
                      if (f.readonly) return false;
                      return true;
                    })
                    .map((field) => {
                      // Interface estendida para suportar campos select com options e default
                      const fieldWithKey = field as unknown as { 
                        key: string; 
                        label: string; 
                        type: string; 
                        required?: boolean;
                        placeholder?: string;
                        description?: string;
                        options?: Array<{ label: string; value: string }>;
                        default?: string;
                      };
                      return (
                        <div key={fieldWithKey.key} className="space-y-2">
                          <Label htmlFor={fieldWithKey.key}>
                            {fieldWithKey.label}
                            {fieldWithKey.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          
                          {/* Renderização condicional: Select para type='select', Input para outros */}
                          {fieldWithKey.type === 'select' && fieldWithKey.options ? (
                            <Select
                              value={credentials[fieldWithKey.key] || fieldWithKey.default || ''}
                              onValueChange={(value) => setCredentials(prev => ({
                                ...prev,
                                [fieldWithKey.key]: value
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={fieldWithKey.placeholder || `Selecione ${fieldWithKey.label.toLowerCase()}...`} />
                              </SelectTrigger>
                              <SelectContent>
                                {fieldWithKey.options.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id={fieldWithKey.key}
                              type={fieldWithKey.type === 'password' ? 'password' : 'text'}
                              placeholder={fieldWithKey.placeholder}
                              value={credentials[fieldWithKey.key] || fieldWithKey.default || ''}
                              onChange={(e) => setCredentials(prev => ({
                                ...prev,
                                [fieldWithKey.key]: e.target.value
                              }))}
                            />
                          )}
                          
                          {fieldWithKey.description && (
                            <p className="text-xs text-muted-foreground">{fieldWithKey.description}</p>
                          )}
                        </div>
                      );
                    })}
                    
                  {/* Mensagem quando todos os campos são OAuth (readonly) */}
                  {isOAuthProvider && credentialsSchema.filter((f) => {
                    const field = f as unknown as { readonly?: boolean; hidden?: boolean; type?: string };
                    return !field.readonly && !field.hidden && field.type !== 'hidden';
                  }).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nenhum campo de credencial necessário. As credenciais serão obtidas via OAuth.
                    </p>
                  )}
                </>
              )}

              {/* Alerta de Instance ID duplicado */}
              {duplicateError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Instance ID já em uso</AlertTitle>
                  <AlertDescription className="mt-2 space-y-2">
                    <p>Este Instance ID já está vinculado a outra instância:</p>
                    <div className="bg-destructive/10 rounded-md p-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ID da Instância:</span>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {duplicateError.id.substring(0, 8).toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tenant:</span>
                        <span className="font-medium">{duplicateError.tenantName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Slug:</span>
                        <span className="font-mono text-xs">{duplicateError.tenantSlug}</span>
                      </div>
                      {duplicateError.instanceName && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Nome:</span>
                          <span>{duplicateError.instanceName}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant="secondary" className="text-xs">
                          {duplicateError.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs mt-2">
                      Para usar este Instance ID, primeiro remova-o da instância acima ou use um Instance ID diferente.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="instanceName">Nome da Instância (opcional)</Label>
                <Input
                  id="instanceName"
                  placeholder="Nome amigável para identificação"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                />
              </div>
            </div>

            <Separator />

            {/* Status de Pagamento */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Status de Pagamento
              </Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="free_trial">Free 7 dias</SelectItem>
                  <SelectItem value="courtesy">Cortesia</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                  <SelectItem value="refunded">Reembolsado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notas */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notas internas</Label>
              <Textarea
                id="notes"
                placeholder="Observações sobre esta instância..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          
          {instance.status === 'pending' ? (
            <Button 
              onClick={handleRelease} 
              disabled={isLoading || !canRelease}
              className="gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unlock className="h-4 w-4" />
              )}
              Liberar para Tenant
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={isLoading} className="gap-2">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Salvar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
